import _ from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { TYPE_EVENT_OTHER } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { checkAndProcessBeatData, countBeatChannels } from '@/entities/beat';
import { checkAndProcessEcgData, formatDrawMutation } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import { formatDrawDisplay } from './helper';
import type { EditDeviceStripModalProps } from './editStripModal';

/**
 * Drives the device strip edit modal, downloading the ECG window around the strip and exposing the capture, save and cancel behaviour. Seeds local state from the incoming strip data, refetches ECG and beat data whenever the strip start or modal visibility changes, builds the viewer object from the response, and hands back the viewer ref, loading state and modal callbacks.
 *
 * @param props - Modal props with the strip data, ECG data map, viewer config, timezone offset and the parent close and update callbacks.
 * @returns An object carrying the modal state, the ECG viewer object, the viewer and strip-epoch refs, and the save, cancel, capture, capture-mode, submit and retry handlers.
 */
const useEditDeviceStripModal = ({
  visible,
  isDeviceEvent = false,
  data,
  originalData,
  updateEditData,
  ecgDataMap,
  timezoneOffset,
  ecgViewerConfig,
  handleClickCloseButton,
}: EditDeviceStripModalProps) => {
  const [state, setState] = useMergeState<{
    isLoading: boolean;
    stripImgs: any[];
    draw: any[];
    stripStart: any;
    aiChannel: any;
    isUpdated: boolean;
    hrInfo?: any;
    realtimeData?: any;
    viewerObjectVersion?: any;
  }>({
    isLoading: false,
    stripImgs: [],
    draw: [],
    stripStart: undefined,
    aiChannel: undefined,
    isUpdated: false,
  });

  const [ecgObject, setEcgObject] = useState<any>({});

  const aiECGViewerRef = useRef<any>(null);
  const startStripEpoch = useRef<any>(null);
  const stopStripEpoch = useRef<any>(null);

  let isActiveQuery: boolean;

  /**
   * Commits the edited device strip back to the parent editor. Sends the captured images, draw widgets and heart-rate info only when a capture has been made and an empty payload otherwise, then resets the viewer interaction mode.
   *
   * @returns Nothing.
   */
  const onClickSave = () => {
    const updateData = state.isUpdated
      ? {
          stripImgs: state.stripImgs,
          draw: state.draw,
          hrInfo: state.hrInfo,
          isUpdateStripImage: true,
        }
      : {};
    updateEditData(updateData);
    if (aiECGViewerRef.current) {
      aiECGViewerRef.current.resetInMode();
    }
  };

  /**
   * Discards the pending strip capture and closes the edit modal. Clears the captured images and draw widgets from local state, invokes the close handler passed in props, and resets the viewer interaction mode.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    setState({
      isUpdated: false,
      stripImgs: [],
      draw: [],
    });
    handleClickCloseButton();
    if (aiECGViewerRef.current) {
      aiECGViewerRef.current.resetInMode();
    }
  };

  /**
   * Builds the ECG viewer descriptor for a device strip from the downloaded samples, beats and AI beat statuses. Computes the jump offset from the strip start epoch against the downloaded window, reformats the stored draw widgets through formatDrawDisplay, pushes gain and filter values into the shared viewer config store, then stores the assembled object in local state.
   *
   * @param ecg - Channel-major sample arrays for the downloaded ECG window.
   * @param beats - Beat positions returned for the window, fed to the viewer as the AI prediction.
   * @param hesBeatStatus - Per-beat classification statuses paired with the beat positions.
   * @param stripStart - Timestamp of the strip start, used to compute the sample offset to jump to.
   * @param exportEcgData - Raw ECG payload carried through so the viewer can export it.
   * @returns Nothing.
   */
  const createObjectEcg = (
    ecg: any,
    beats: any,
    hesBeatStatus: any,
    stripStart: any,
    exportEcgData: any,
  ) => {
    const { gain: configGain, hpFilter, lpFilter } = ecgViewerConfig || {};
    const { channels, gain, samplingFrequency } = ecgDataMap;
    // *: Add capture sample mark
    let drawStartSample = 0;
    const stripStartEpoch = moment(stripStart).valueOf();
    if (stripStartEpoch >= startStripEpoch.current && stripStartEpoch <= stopStripEpoch.current) {
      drawStartSample = Math.round(
        ((stripStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    const { draw, startSample } = formatDrawDisplay(
      state.draw,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    if (!_.isNil(startSample)) {
      drawStartSample = startSample;
    }
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    if (!_.isNil(configGain)) {
      ECG_VIEWER_CONFIG_STORE.ampValue = configGain;
    }
    if (!_.isNil(hpFilter)) {
      ECG_VIEWER_CONFIG_STORE.hiPValue = hpFilter;
    }
    if (!_.isNil(lpFilter)) {
      ECG_VIEWER_CONFIG_STORE.loPValue = lpFilter;
    }
    setEcgObject({
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      viewerObject,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: undefined,
      aiPredict: [
        {
          beats,
          hesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(hesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: drawStartSample,
      hiddenChannels: [0, 1, 2],
      hpFilters: hpFilter ?? ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: lpFilter ?? ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: 25,
      amp: configGain ?? ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: 10,
      exportEcgData,
    });
  };

  /**
   * Fetches the ECG samples and beats for a 300 second window centred on the strip start. Floors the strip start onto the sampling grid, prefers the realtime start and stop bounds for a device event, requests ECG and beat data in parallel with a 10 second margin that is trimmed off each end, counts the beat channels and builds the viewer object; a cancellation is swallowed and the loading flag always clears.
   *
   * @param isRetry - When true the result is applied even if the effect that started the request has been torn down.
   * @returns A promise resolving once the ECG data has been fetched and the viewer object built.
   */
  const downloadDataEcg = async (isRetry = false) => {
    setState({ isLoading: true });
    try {
      let convertStripStart = moment(state.stripStart);
      const stripStartMillisecond = convertStripStart.millisecond();
      const dividedNumber = 1000 / ecgDataMap.samplingFrequency;
      const floorNumber = Math.floor(stripStartMillisecond / dividedNumber) * dividedNumber;
      convertStripStart = convertStripStart.set('ms', floorNumber);
      const startStrip =
        isDeviceEvent && state.realtimeData?.start
          ? moment(state.realtimeData?.start)
          : convertStripStart.clone().add(-150, 's');
      const stopStrip =
        isDeviceEvent && state.realtimeData?.stop
          ? moment(state.realtimeData?.stop)
          : convertStripStart.clone().add(150, 's');
      const momentObject = {
        startMoment: startStrip,
        stopMoment: stopStrip,
      };
      const dataLength =
        ((stopStrip.valueOf() - startStrip.valueOf()) / 1000) * ecgDataMap.samplingFrequency;
      const exportMoment = {
        startMoment: moment(momentObject.startMoment).subtract(10, 'seconds'),
        stopMoment: moment(momentObject.stopMoment).add(10, 'seconds'),
      };
      const [ecgData, holterBeats] = (await Promise.all([
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength,
          momentObject: exportMoment,
        } as any),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject,
        } as any),
      ])) as any;
      const addDataLength = 10 * ecgDataMap.samplingFrequency;
      const drawEcg = _.map(ecgData, (data: any) => _.slice(data, addDataLength, -addDataLength));
      if (isActiveQuery || isRetry) {
        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        const channel = countBeatChannels(holterBeats?.beatChannels);
        createObjectEcg(
          drawEcg,
          holterBeats?.beats || [],
          holterBeats?.hesBeatStatus || [],
          state.stripStart,
          { ecgData },
        );
        setState({
          aiChannel: channel,
          isLoading: false,
        });
      }
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setState({ isLoading: false });
    }
  };

  /**
   * Triggers the AI ECG viewer to render and submit the current strip capture. Schedules a 50 ms timeout that calls handleSubmit on the viewer ref when it is mounted.
   *
   * @returns Nothing.
   */
  const callCreateImage = () => {
    setTimeout(() => {
      if (aiECGViewerRef.current) {
        aiECGViewerRef.current.handleSubmit();
      }
    }, 50);
  };

  /**
   * Stores the SVG strips captured from the viewer together with their normalized draw widgets. Skips an empty capture, converts the draw coordinates to timestamps with formatDrawMutation against the downloaded window start, marks the strip as updated for both the event and other capture modes, then schedules the viewer submit; a failure is logged and surfaced as an error toast.
   *
   * @param draw - Draw widgets captured from the viewer, in sample coordinates.
   * @param allIndexStripSvg - Captured strip images, one per visible strip index.
   * @param captureMode - Capture mode the viewer ran in, event or other.
   * @param visibleChannels - Channels visible at capture time, used when normalizing the draw data.
   * @returns Nothing.
   */
  const handleCaptureImages = ({ draw, allIndexStripSvg, captureMode, visibleChannels }: any) => {
    try {
      if (allIndexStripSvg.length > 0) {
        const { samplingFrequency } = ecgDataMap;
        const { formatDraw } = formatDrawMutation(
          draw,
          samplingFrequency,
          startStripEpoch.current,
          timezoneOffset,
          visibleChannels,
        );
        if (captureMode === CAPTURE_MODE.EVENT) {
          setState({
            stripImgs: allIndexStripSvg,
            draw: formatDraw,
            isUpdated: true,
          });
        } else if (captureMode === CAPTURE_MODE.OTHER) {
          setState({
            stripImgs: allIndexStripSvg,
            draw: formatDraw,
            isUpdated: true,
          });
        }
        callCreateImage();
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Decides which capture mode the strip editor uses for the current device event. Returns the event mode unless the original data carries the other-event type, in which case the other mode is used.
   *
   * @returns The capture mode constant matching the current event type.
   */
  const getCaptureMode = () => {
    if (originalData?.type !== TYPE_EVENT_OTHER) {
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };

  /**
   * Captures the viewer state emitted on submit as the heart-rate info for the edited strip. Reads the beat markers and display settings off the payload, converts the viewer widgets into timestamped draw data with formatDrawMutation, records the resulting strip start and stop as a single strip time entry, and stores the whole bundle in local state.
   *
   * @param ecgData - Viewer payload whose ecg field carries the widgets, beat markers and display settings.
   * @returns Nothing.
   */
  const onSubmit = (ecgData: any) => {
    const {
      beatMarkers,
      viewerObject,
      speed,
      amp,
      hpFilters,
      notchFilters,
      hiddenChannels,
      isShowBeatMarkers,
      isAppliedAIByDefault,
      isAIBeatMarkers,
      diagnosticLead,
      hesBeatStatus,
      isShowVES,
      isShowSVES,
      wndDuration,
      samplingRate,
    } = ecgData.ecg;
    const stripTimeObjects = [];
    const { samplingFrequency } = ecgDataMap;

    const { stripStart, stripStop, formatDraw } = formatDrawMutation(
      viewerObject.widgets,
      samplingFrequency,
      startStripEpoch.current,
      timezoneOffset,
      hiddenChannels,
    );
    stripTimeObjects.push({
      stripStart,
      stripStop,
    });
    viewerObject.widgets = formatDraw;
    const hrInfo = {
      viewerObject,
      beatMarkers,
      speed,
      amp,
      wndDuration,
      hpFilters,
      notchFilters,
      hiddenChannels,
      isShowBeatMarkers,
      isAppliedAIByDefault,
      isAIBeatMarkers,
      diagnosticLead,
      hesBeatStatus,
      isShowVES,
      isShowSVES,
      stripTimeObjects,
    };
    setState({ hrInfo });
  };

  /**
   * Re-runs the ECG download after a failed load. Calls downloadDataEcg with the retry flag so the response is applied regardless of the active-query guard.
   *
   * @returns Nothing.
   */
  const onClickRetry = () => {
    downloadDataEcg(true);
  };

  useEffect(() => {
    if (data) {
      const { stripStart } = originalData;
      const { stripImgs, draw, realtimeData, viewerObjectVersion } = data;
      const setStateObject = {
        stripImgs,
        draw,
        stripStart,
        realtimeData,
        viewerObjectVersion,
        isUpdated: false,
      };
      setState(setStateObject);
    }
  }, [data, originalData]);

  useEffect(() => {
    isActiveQuery = true;
    if (state.stripStart && visible) {
      downloadDataEcg();
    } else {
      setEcgObject({});
    }
    return () => {
      isActiveQuery = false;
    };
  }, [state.stripStart, isDeviceEvent, visible]);

  return {
    state,
    ecgObject,
    aiECGViewerRef,
    startStripEpoch,
    onClickSave,
    onClickCancel,
    handleCaptureImages,
    getCaptureMode,
    onSubmit,
    onClickRetry,
  };
};

export default useEditDeviceStripModal;
